import { Droplet } from 'lucide-react';
import type { AlertDetailResponse } from '@/types/alerts';
import {
  EvidenceRow,
  HeadlineLine,
  MissingContextLine,
  RendererShell,
  formatDateTime,
  getNum,
  getStr,
} from './_shared';

/**
 * OIL-3 — Orphaned Oil Transaction.
 *
 * "Oil bought without a fuel transaction within ±24h on the same card."
 * This is the canonical airtime-substitution pattern: the merchant rings
 * up an oil SKU but no actual fuel changes hands.
 *
 * Evidence keys (verified from live fraud.alert sample):
 *   rule_name, site_name, driver_name, window_hours, card_reference,
 *   site_reference, driver_reference, transaction_datetime,
 *   vehicle_registration, no_fuel_txn_in_window, transaction_reference
 */
export default function OilOrphanedRenderer(
  evidence: Record<string, unknown> | null | undefined,
  alert: AlertDetailResponse,
): JSX.Element {
  const windowHours = getNum(evidence, 'window_hours') ?? 24;
  const cardRef =
    getStr(evidence, 'card_reference') ?? alert.card_token ?? null;
  const siteName = getStr(evidence, 'site_name');
  const txnTime = getStr(evidence, 'transaction_datetime');
  const vehicleReg =
    getStr(evidence, 'vehicle_registration') ?? alert.asset_token ?? null;
  const driverName = getStr(evidence, 'driver_name');
  const noFuelInWindow = evidence?.no_fuel_txn_in_window === true;

  return (
    <RendererShell>
      <div className="flex items-start gap-2">
        <Droplet className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
        <HeadlineLine>
          Oil purchased without any matching fuel transaction on the same card
          in a {windowHours}-hour window — classic airtime-substitution
          pattern.
        </HeadlineLine>
      </div>

      <div className="rounded-md border bg-muted/20 px-3 py-2">
        <EvidenceRow label="Card">
          <span className="font-mono text-xs">{cardRef ?? '—'}</span>
        </EvidenceRow>
        <EvidenceRow label="Vehicle">
          <span className="font-mono text-xs">{vehicleReg ?? '—'}</span>
        </EvidenceRow>
        <EvidenceRow label="Site">{siteName ?? '—'}</EvidenceRow>
        <EvidenceRow label="Driver">{driverName ?? '—'}</EvidenceRow>
        <EvidenceRow label="Oil purchased at">
          {formatDateTime(txnTime)}
        </EvidenceRow>
        <EvidenceRow label={`Fuel txn within ±${windowHours}h`}>
          {noFuelInWindow ? (
            <span className="font-medium text-red-700">None found</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </EvidenceRow>
      </div>

      {!cardRef && !vehicleReg && (
        <MissingContextLine message="Card and vehicle references are missing — investigate the upstream feed." />
      )}
    </RendererShell>
  );
}
